import { memo, useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import {
  selectIsSavingWorkingProject,
  selectWorkingProjectRegistrationSettings,
} from '@/store/slices/project/selectors';
import SettingsToggle from '../../../../controls/settings-toggle';
import resolveAutomaticSSOLoginToggle from './helpers/resolve-automatic-sso-login-toggle';

/**
 * Project-level opt-in telling the live event to send unauthenticated
 * visitors straight into the SSO provider's login flow instead of rendering the sign-in button
 */
const AutomaticSSOLoginToggle = memo(() => {
  const dispatch = useAppDispatch();
  const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);

  const on = resolveAutomaticSSOLoginToggle(registrationSettings);

  const handleToggle = useCallback(
    (_sKey: string, nextOn: boolean) => {
      if (!registrationSettings) return;
      dispatch(
        saveWorkingProjectByFields({
          registration_settings: {
            ...registrationSettings,
            singleSignOn: {
              ...(registrationSettings?.singleSignOn ?? { isOn: false, displayTop: true }),
              automaticSSOLogin: nextOn,
            },
          },
        })
      );
    },
    [dispatch, registrationSettings]
  );

  return (
    <SettingsToggle
      label="Automatic User Login"
      value={on}
      sKey="automaticSSOLogin"
      onToggle={handleToggle}
      disabled={savingWorkingProject || !registrationSettings}
      tooltipText="Send visitors straight to your SSO provider to sign in instead of showing them the sign-in page first"
    />
  );
});

AutomaticSSOLoginToggle.displayName = 'AutomaticSSOLoginToggle';

export default AutomaticSSOLoginToggle;
